/*
  Zaktualizowano Header:
  1. Przeniesiono ikonę Pomocy (FiHelpCircle) poza pole nazwy projektu (na jego prawą stronę).
*/
import React, { useState, useRef } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { RiUserLine, RiStackLine } from 'react-icons/ri';
import ExportDialog from './ExportDialog';
import MyProjectsPanel from './MyProjectsPanel';
import SettingsDialog from './SettingsDialog';
import ProfileDialog from './ProfileDialog';
import StyleSelectionDialog from './StyleSelectionDialog';
import exportManager from './ExportManager';
import projectManager from '../core/ProjectManager';
import useSceneStore from '../store/useSceneStore';
import useAuthStore from '../store/useAuthStore';
import { applyStyle } from '../core/styleManager';
import useNotificationStore from '../store/useNotificationStore';
import useDialogStore from '../store/useDialogStore';
import { PLAN_IDS } from '../core/subscriptionManager';

const { FiDownload, FiUpload, FiSave, FiFileText, FiFilePlus, FiSettings, FiAlertTriangle, FiHelpCircle } = FiIcons;

const Header = () => {
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isMyProjectsOpen, setIsMyProjectsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isStyleDialogOpen, setIsStyleDialogOpen] = useState(false);
  const [profileInitialTab, setProfileInitialTab] = useState('settings');
  const { projectName, setProjectName } = useSceneStore();
  const planId = useAuthStore((state) => state.planId);
  const { addNotification } = useNotificationStore();
  const { openDialog } = useDialogStore();
  const fileInputRef = useRef(null);

  const preserveProjectBeforePricing = () => {
    try {
      sessionStorage.setItem(
        'floorcraft-payment-draft',
        JSON.stringify(projectManager.getProjectState())
      );
    } catch (error) {
      console.error('Nie udało się zachować aktualnego rzutu', error);
    }

    setProfileInitialTab('pricing');
    setIsProfileOpen(true);
  };

  const handleExportImage = async (options) => {
    try {
      if (options.format === 'json') {
        projectManager.exportProject(); return;
      }
      const canvas = document.querySelector('#main-canvas');
      if (!canvas) { addNotification('error', 'Nie znaleziono obszaru roboczego (Canvas).'); return; }
      exportManager.setCanvas(canvas);
      switch (options.format) {
        case 'jpg': case 'png': await exportManager.exportToImage(options.format, options.quality, options.resolution, options.backgroundColor); break;
        default: throw new Error('Unsupported format');
      }
    } catch (error) { console.error('Export failed:', error); addNotification('error', 'Eksport nie powiódł się. Spróbuj ponownie.'); }
  };

  const handleSaveProject = async () => {
    if (planId === PLAN_IDS.FREE) {
      preserveProjectBeforePricing();
      return;
    }

    const result = await projectManager.saveToDatabase('');

    if (result?.success) {
      addNotification('success', `Projekt "${projectName}" został zapisany w bazie danych.`);
    } else {
      addNotification('error', result?.error || 'Nie udało się zapisać projektu.');
    }
  };

  const handleLoadProjectClick = () => fileInputRef.current?.click();

  const handleNewProject = () => {
    openDialog({
      title: 'Nowy Projekt',
      message: 'Czy na pewno chcesz utworzyć nowy projekt? Wszystkie niezapisane zmiany zostaną utracone.',
      confirmText: 'Utwórz nowy',
      variant: 'default',
      onConfirm: () => {
        if (planId === 'AGENCY') setIsStyleDialogOpen(true);
        else { projectManager.newProject(); addNotification('info', 'Utworzono nowy, pusty projekt.'); }
      }
    });
  };

  const handleStyleConfirm = (styleId) => {
    setIsStyleDialogOpen(false); projectManager.newProject(); applyStyle(styleId); addNotification('success', 'Utworzono nowy projekt z wybranym stylem.');
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    try { await projectManager.importProject(file); addNotification('success', 'Projekt został wczytany pomyślnie.'); e.target.value = ''; }
    catch (error) { console.error(error); addNotification('error', 'Błąd podczas wczytywania projektu.'); }
  };

  const handleProfileClick = () => { setProfileInitialTab('settings'); setIsProfileOpen(true); };
  const handleProBannerClick = () => { setProfileInitialTab('pricing'); setIsProfileOpen(true); };
  const handleExportClick = () => {
    if (planId === PLAN_IDS.FREE) {
      preserveProjectBeforePricing();
      return;
    }

    setIsExportOpen(true);
  };

  return (
    <>
      <div className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 z-10 relative shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3">
            <img
              src="https://media.swipepages.com/2025/11/615ca9d98dcf7f0011c44053/rzut-nieruchomosci-l.png"
              alt="Rzut Nieruchomości"
              className="h-8 w-auto max-w-[190px] object-contain"
            />
            <div className="h-6 w-px bg-gray-300 mx-1"></div>
            <div className="flex items-center gap-2">
              <div className="flex items-center group relative">
                 <SafeIcon icon={FiFileText} className="text-gray-400 absolute left-2 text-sm" />
                 <input 
                   type="text" 
                   value={projectName} 
                   onChange={(e) => setProjectName(e.target.value)} 
                   placeholder="Nazwa projektu" 
                   className="pl-8 pr-3 py-1.5 text-sm font-medium text-gray-700 border border-transparent hover:border-gray-300 focus:border-[#1E2937] rounded-md transition-all bg-transparent hover:bg-gray-50 focus:bg-white focus:ring-0 w-48 sm:w-64 outline-none" 
                 />
              </div>
              <a 
                href="https://pomoc.rzutnieruchomosci.pl/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-400 hover:text-[#1E2937] transition-colors p-1.5 hover:bg-gray-100 rounded-lg" 
                title="Pomoc"
              >
                <SafeIcon icon={FiHelpCircle} className="text-xl" />
              </a>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".json" className="hidden" />
          <button onClick={handleNewProject} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors hidden sm:flex" title="Utwórz nowy projekt"><SafeIcon icon={FiFilePlus} className="text-sm" /><span className="hidden md:inline">Nowy rzut</span></button>
          <button onClick={handleLoadProjectClick} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors hidden sm:flex" title="Wczytaj projekt z pliku JSON"><SafeIcon icon={FiUpload} className="text-sm" /><span className="hidden md:inline">Wczytaj rzut</span></button>
          <button onClick={handleSaveProject} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors" title="Zapisz projekt w bazie danych"><SafeIcon icon={FiSave} className="text-sm" /><span className="hidden sm:inline">Zapisz</span></button>
          <div className="h-6 w-px bg-gray-300 mx-1"></div>
          <button onClick={() => setIsSettingsOpen(true)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Ustawienia Domyślne"><SafeIcon icon={FiSettings} className="text-xl" /></button>
          <button onClick={() => setIsMyProjectsOpen(true)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600" title="Moje Rzuty">
            <SafeIcon icon={RiStackLine} className="text-xl" />
          </button>
          <button onClick={handleProfileClick} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Mój profil"><SafeIcon icon={RiUserLine} className="text-xl" /></button>
          <button onClick={handleExportClick} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-[#1E2937] text-white hover:bg-gray-800 rounded-lg shadow-sm" title="Eksportuj rzut"><SafeIcon icon={FiDownload} />Eksportuj</button>
        </div>
      </div>
      {planId === PLAN_IDS.FREE && ( <div className="bg-yellow-100 text-yellow-800 text-sm py-2 px-4 text-center border-b border-yellow-200 flex items-center justify-center gap-2 relative z-0"><SafeIcon icon={FiAlertTriangle} className="text-yellow-600" /><span>Odblokuj pełne możliwości — <button onClick={handleProBannerClick} className="font-bold underline ml-1 hover:text-yellow-900 cursor-pointer bg-transparent border-none p-0 inline">przejdź na PRO</button> &nbsp;już za 29 zł.</span></div> )}
      <ExportDialog isOpen={isExportOpen} onClose={() => setIsExportOpen(false)} onExport={handleExportImage} />
      <MyProjectsPanel isOpen={isMyProjectsOpen} onClose={() => setIsMyProjectsOpen(false)} />
      <SettingsDialog isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
      <ProfileDialog isOpen={isProfileOpen} onClose={() => setIsProfileOpen(false)} initialTab={profileInitialTab} />
      <StyleSelectionDialog isOpen={isStyleDialogOpen} onClose={() => setIsStyleDialogOpen(false)} onConfirm={handleStyleConfirm} />
    </>
  );
};

export default Header;